import React, { useRef, useState, forwardRef } from "react";
import clsx from "clsx";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { Button } from "@curves/ui-base";
import GradientColorHeader from "@icoach/components/dialog/GradientColorHeader";
import { isFunction, refIsRequiredError } from "@util/utils";
import { InvoiceByCarrierNo, InvoiceByCarrierType, InvoiceByEmail, InvoiceByIssuingType, InvoiceByTriplicateUniform, useInvoiceFieldVisibility } from "@icoach/components/invoice";
import { INVOICE_ISSUING_TYPE_OPTIONS, INVOICE_CARRIER_TYPE_OPTIONS } from "@icoach/pos/PosScreenPage.config";
import { INVOICE_CARRIER_TYPE, INVOICE_ISSUING_TYPE } from "@constants/";

const InvoiceSettingDialog = forwardRef(({ className, open, sourceData, onClose, refresh, options }, ref) => {
	if (!open) return null;

	const { taxID, taxTitle, invoiceIssuingTypeOptions, invoiceCarrierTypeOptions, invoiceCarrierNo, issuingEmail } = sourceData || {};

	// 從 options 中解構出發票相關選項
	const { invoiceTypeOption = [], invoiceCarrierTypeOption = [] } = options || {};

	// 使用統一的選項配置，優先級：集中式選項 > sourceData選項 > 預設配置
	const issuingTypeOptions = invoiceTypeOption.length > 0 ? invoiceTypeOption : invoiceIssuingTypeOptions || INVOICE_ISSUING_TYPE_OPTIONS;
	const carrierTypeOptions = invoiceCarrierTypeOption.length > 0 ? invoiceCarrierTypeOption : invoiceCarrierTypeOptions || INVOICE_CARRIER_TYPE_OPTIONS;

	// 發票相關狀態
	const [issuingType, setIssuingType] = useState(sourceData?.issuingType || INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE);
	const [invoiceCarrierType, setInvoiceCarrierType] = useState(sourceData?.invoiceCarrierType || INVOICE_CARRIER_TYPE.MEMBERCARRIER);

	// 使用 useInvoiceFieldVisibility hook 管理欄位顯示邏輯
	const fieldVisibility = useInvoiceFieldVisibility({
		issuingType,
		invoiceCarrierType,
	});

	// 發票元件 refs
	const invoiceByIssuingTypeRef = useRef(null);
	const invoiceByTriplicateUniformRef = useRef(null);
	const invoiceByCarrierTypeRef = useRef(null);
	const invoiceByCarrierNoRef = useRef(null);
	const invoiceNotificationEmailRef = useRef(null);

	// 處理發票類型變更
	const handleIssuingTypeChange = value => {
		setIssuingType(value);
		// 當發票類型變更時，重置載具類型為預設值
		if (value === INVOICE_ISSUING_TYPE.DUPLICATEUNIFORMINVOICE) {
			// 二聯式發票
			setInvoiceCarrierType(INVOICE_CARRIER_TYPE.MEMBERCARRIER);
		}
	};

	// 處理載具類型變更
	const handleInvoiceCarrierTypeChange = value => {
		setInvoiceCarrierType(value);
	};

	// 取得所有表單結果
	const getResult = () => {
		let result = {};

		// 發票類型
		if (invoiceByIssuingTypeRef.current?.getResult) {
			const { issuingType } = invoiceByIssuingTypeRef.current.getResult();
			result.issuingType = issuingType;
		}

		// 三聯式發票
		if (fieldVisibility.showTriplicateUniformField && invoiceByTriplicateUniformRef.current?.getResult) {
			const { taxID, taxTitle } = invoiceByTriplicateUniformRef.current.getResult();
			result.taxID = taxID;
			result.taxTitle = taxTitle;
		}

		// 載具類型
		if (fieldVisibility.showCarrierTypeField && invoiceByCarrierTypeRef.current?.getResult) {
			const { carrierType } = invoiceByCarrierTypeRef.current.getResult();
			result.invoiceCarrierType = carrierType;
		}

		// 載具號碼
		if (fieldVisibility.showCarrierNoField && invoiceByCarrierNoRef.current?.getResult) {
			const { carrierNo } = invoiceByCarrierNoRef.current.getResult();
			result.invoiceCarrierNo = carrierNo;
		}

		// 發票通知信箱
		if (invoiceNotificationEmailRef.current?.getResult) {
			const { issuingEmail } = invoiceNotificationEmailRef.current.getResult();
			result.issuingEmail = issuingEmail;
		}

		return result;
	};

	// 檢查是否有錯誤
	const isError = () => {
		const refs = [
			invoiceByIssuingTypeRef,
			fieldVisibility.showTriplicateUniformField && invoiceByTriplicateUniformRef,
			fieldVisibility.showCarrierTypeField && invoiceByCarrierTypeRef,
			fieldVisibility.showCarrierNoField && invoiceByCarrierNoRef,
			invoiceNotificationEmailRef,
		].filter(Boolean);

		return refIsRequiredError(...refs);
	};

	const handleSaveClick = () => {
		if (!isError()) {
			let params = getResult();
			isFunction(refresh) && refresh(params);
			isFunction(onClose) && onClose();
		}
	};

	return (
		<Dialog className={clsx("simple-content", "dialog", className)} PaperProps={{ className: "wx-40rem" }} open={open}>
			<GradientColorHeader onClose={onClose}>發票設定</GradientColorHeader>
			<DialogContent>
				<Grid spacing={2} container>
					{/* 發票類型選擇 */}
					<Grid xs={12} item>
						<InvoiceByIssuingType ref={invoiceByIssuingTypeRef} issuingType={issuingType} options={issuingTypeOptions} onChange={handleIssuingTypeChange} />
					</Grid>

					{/* 三聯式發票 */}
					{fieldVisibility.showTriplicateUniformField && (
						<Grid xs={12} item>
							<InvoiceByTriplicateUniform ref={invoiceByTriplicateUniformRef} taxID={taxID} taxTitle={taxTitle} />
						</Grid>
					)}

					{/* 載具類型 */}
					{fieldVisibility.showCarrierTypeField && (
						<Grid xs={12} item>
							<InvoiceByCarrierType ref={invoiceByCarrierTypeRef} carrierType={invoiceCarrierType} options={carrierTypeOptions} onChange={handleInvoiceCarrierTypeChange} />
						</Grid>
					)}

					{/* 載具號碼 */}
					{fieldVisibility.showCarrierNoField && (
						<Grid xs={12} item>
							<InvoiceByCarrierNo ref={invoiceByCarrierNoRef} carrierNo={invoiceCarrierNo} carrierType={invoiceCarrierType} />
						</Grid>
					)}

					{/* 發票通知信箱 */}
					<Grid xs={12} item>
						<InvoiceByEmail ref={invoiceNotificationEmailRef} issuingEmail={issuingEmail} required />
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleSaveClick} variant="contained">
					確認儲存
				</Button>
			</DialogActions>
		</Dialog>
	);
});

InvoiceSettingDialog.displayName = "InvoiceSettingDialog";

export default InvoiceSettingDialog;
